import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Star, Loader2, ClipboardCheck, CheckCircle2 } from "lucide-react";

function StarRating({ value, onChange, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button"
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(value === n ? 0 : n)}
          className="p-0.5 transition-transform hover:scale-110"
          data-testid={`${testid}-star-${n}`}
          aria-label={`${n} stelle`}>
          <Star className="w-6 h-6" fill={(hover || value) >= n ? "#F59E0B" : "none"}
            style={{ color: (hover || value) >= n ? "#F59E0B" : "#D1D5DB" }} strokeWidth={1.5} />
        </button>
      ))}
      <span className="text-xs ml-2 self-center font-mono" style={{ color: "var(--text-muted)" }}>
        {value > 0 ? `${value}/5` : "—"}
      </span>
    </div>
  );
}

export default function CheckQualityBlock({ cid }) {
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cq, tpl] = await Promise.all([
          api.get(`/clienti/${cid}/check-quality`),
          api.get("/check-quality-items?only_active=true"),
        ]);
        setTemplate(tpl.data);
        // Merge template with saved items (preserve rating/note by item_id)
        const savedMap = {};
        (cq.data.items || []).forEach((i) => { savedMap[i.item_id] = i; });
        const merged = tpl.data.map((t) => savedMap[t.id] || { item_id: t.id, label: t.label, rating: 0, note: "" });
        setData({ ...cq.data, items: merged });
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [cid]);

  const setItem = (idx, patch) => {
    setData((d) => {
      const items = [...d.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...d, items };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clienti/${cid}/check-quality`, {
        items: data.items,
        note_generali: data.note_generali || "",
        completed: !!data.completed,
      });
      toast.success("Check Quality salvato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  const rated = data.items.filter((i) => i.rating > 0).length;
  const avg = rated > 0 ? (data.items.reduce((s, i) => s + (i.rating || 0), 0) / rated).toFixed(1) : "—";

  return (
    <form onSubmit={save} className="space-y-5" data-testid="checkquality-form">
      <div className="card-shell p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5" style={{ color: "var(--primary)" }} />
          <h2 className="font-bold text-lg flex-1" style={{ fontFamily: "Manrope" }}>Check Quality</h2>
          <div className="text-sm" data-testid="cq-avg">
            <span className="font-bold text-xl" style={{ color: "var(--primary)" }}>{avg}</span>
            <span className="text-xs ml-1" style={{ color: "var(--text-muted)" }}>media · {rated}/{data.items.length} voci valutate</span>
          </div>
        </div>

        {template.length === 0 ? (
          <div className="p-6 text-center rounded-md" style={{ background: "var(--surface-muted)" }}>
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>
              Nessuna voce di controllo qualità configurata.
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Un admin può aggiungerle da <strong>Impostazioni → Check Quality</strong>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {data.items.map((it, idx) => (
              <div key={it.item_id} className="p-4 rounded-md border" style={{ borderColor: "var(--border)" }} data-testid={`cq-item-${it.item_id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
                  <div className="flex-1 font-semibold text-sm">{it.label}</div>
                  <StarRating value={it.rating || 0} onChange={(v) => setItem(idx, { rating: v })} testid={`cq-rating-${it.item_id}`} />
                </div>
                <input value={it.note || ""} onChange={(e) => setItem(idx, { note: e.target.value })}
                  placeholder="Nota opzionale sulla voce"
                  className="w-full px-3 py-1.5 text-xs rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }}
                  data-testid={`cq-note-${it.item_id}`} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note generali</label>
          <textarea rows={3} value={data.note_generali || ""} onChange={(e) => setData({ ...data, note_generali: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            placeholder="Osservazioni globali, azioni correttive, feedback cliente…"
            data-testid="cq-note-gen" />
        </div>

        <div className="flex items-center justify-between mt-5 gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!data.completed} onChange={(e) => setData({ ...data, completed: e.target.checked })}
              data-testid="cq-completed" />
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" style={{ color: data.completed ? "#16A34A" : "var(--text-muted)" }} />
              Check Quality completato
            </span>
          </label>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="cq-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva Check Quality
          </button>
        </div>
      </div>
    </form>
  );
}
